'use server';

import logApiError from 'utils/log/logApiError';
import { pianoIdApi, pianoIdEndpoints } from '@/app_actions/externalApiUtils';
import { unpackAndVerifyUserToken } from '@/app_actions/Piano/userTokenActions';
import { pianoErrorCodes } from '@/app_lib/piano/pianoErrorCodes';

interface GoogleUserPayload {
  email: string;
  givenName: string;
  familyName: string;
}

interface PianoUser {
  uid: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

// Piano API response interfaces (keeping snake_case as that's what Piano API returns)
interface PianoApiRegistrationResponse {
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  access_token: string;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  token_type: string;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  refresh_token: string;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  expires_in: number;
  registration: boolean;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  email_confirmation_required: boolean;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  pre_confirmed_user: boolean;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  extend_expired_access_enabled: boolean;
}

interface PianoApiTokenResponse {
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  access_token: string;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  token_type: string;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  expires_in: number;
}

interface PianoAuthResponse {
  success: boolean;
  pianoUid?: string;
  pianoToken?: string;
  isNewUser?: boolean;
  message?: string;
}

interface PianoAuthError {
  success: false;
  message: string;
}

type PianoAuthResult = PianoAuthResponse | PianoAuthError;

async function checkUserExists(email: string): Promise<PianoUser | null> {
  try {
    const requestUrl = `${pianoIdApi(pianoIdEndpoints.getUser)}&email=${encodeURIComponent(email)}`;
    const userCheckResponse = await fetch(requestUrl, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    if (userCheckResponse.ok) {
      const userResponse = (await userCheckResponse.json()) as PianoUser;
      return userResponse || null;
    }
    return null;
  } catch (error) {
    logApiError(error as Error, '[checkUserExists]');
    return null;
  }
}

async function registerGoogleUser(email: string, givenName: string, familyName: string): Promise<PianoUser> {
  try {
    // TODO: This needs to have an actual consent field now that registration source is being handled elsewhere
    // const encodedConsentField = encodeURIComponent(
    //   JSON.stringify({
    //     consent would go here
    //   }),
    // );

    const requestUrl = `${pianoIdApi(pianoIdEndpoints.register)}&email=${encodeURIComponent(email)}&first_name=${encodeURIComponent(givenName || '')}&last_name=${encodeURIComponent(familyName || '')}&is_passwordless=true&password=`;

    const registerResponse = await fetch(requestUrl, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    if (!registerResponse.ok) {
      const errorData = await registerResponse.json();

      // Check for user already exists error
      if (errorData.error_code_list && errorData.error_code_list[0].code === pianoErrorCodes.userExistsError) {
        throw new Error('A user with this email already exists.');
      }

      throw new Error(`Registration failed: ${errorData.error_code_list?.[0]?.message || 'Unknown error'}`);
    }

    const registerResult = (await registerResponse.json()) as PianoApiRegistrationResponse;
    if (!registerResult) {
      throw new Error('Registration succeeded but no data returned');
    }
    if (!registerResult.access_token) {
      throw new Error('Registration succeeded but no access token returned');
    }

    // Unpack the JWT token to get user information
    const unpackedToken = unpackAndVerifyUserToken(registerResult.access_token);
    logApiError(new Error(`Unpacked token: ${JSON.stringify(unpackedToken)}`), '[registerGoogleUser-debug]');

    // Map unpacked token data to our interface
    return {
      email: unpackedToken.email,
      firstName: unpackedToken.given_name,
      lastName: unpackedToken.family_name,
      uid: unpackedToken.sub,
    };
  } catch (error) {
    logApiError(error as Error, '[registerGoogleUser]');
    throw error;
  }
}

async function generateUserToken(uid: string): Promise<string> {
  try {
    // Use the Piano ID API to generate a token for the user
    const requestUrl = `${pianoIdApi('/publisher/token')}&uid=${uid}`;

    const tokenResponse = await fetch(requestUrl, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    if (!tokenResponse.ok) {
      const errorData = await tokenResponse.json();
      throw new Error(`Token generation failed: ${errorData.error_code_list?.[0]?.message || 'Unknown error'}`);
    }

    const tokenResult = (await tokenResponse.json()) as PianoApiTokenResponse;
    return tokenResult.access_token;
  } catch (error) {
    logApiError(error as Error, '[generateUserToken]');
    throw error;
  }
}

/**
 * Validates the payload for Piano authentication via Google user data
 */
function validateGoogleUserPayload(payload: GoogleUserPayload): { isValid: boolean; errorMessage?: string } {
  const { email } = payload;

  if (!email || typeof email !== 'string') {
    return { errorMessage: 'Valid email is required', isValid: false };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { errorMessage: 'Invalid email format', isValid: false };
  }

  return { isValid: true };
}

/**
 * Helper to validate payload and return error result if invalid.
 */
function getPayloadValidationResult(payload: GoogleUserPayload): PianoAuthError | null {
  const validation = validateGoogleUserPayload(payload);
  if (!validation.isValid) {
    return {
      message: validation.errorMessage || 'Invalid payload',
      success: false,
    };
  }
  return null;
}

/**
 * Helper to check user and register if needed.
 */
async function getOrRegisterPianoUser(
  email: string,
  givenName: string,
  familyName: string,
): Promise<{ pianoUser: PianoUser | null; isNewUser: boolean; error?: string }> {
  let pianoUser = await checkUserExists(email);
  let isNewUser = false;
  if (!pianoUser) {
    try {
      pianoUser = await registerGoogleUser(email, givenName || '', familyName || '');
      isNewUser = true;
    } catch (registrationError) {
      const errorMessage =
        registrationError && typeof registrationError === 'object' && 'message' in registrationError
          ? String(registrationError.message)
          : 'Registration failed';
      return { error: errorMessage, isNewUser, pianoUser: null };
    }
  }
  return { isNewUser, pianoUser };
}

/**
 * Helper to generate Piano user token.
 */
async function getPianoUserToken(uid: string): Promise<{ pianoToken: string | null; error?: string }> {
  try {
    const pianoToken = await generateUserToken(uid);
    return { pianoToken };
  } catch (tokenError) {
    const errorMessage =
      tokenError && typeof tokenError === 'object' && 'message' in tokenError
        ? String(tokenError.message)
        : 'Token generation failed';
    return { error: errorMessage, pianoToken: null };
  }
}

/**
 * Server action to authenticate a user in Piano using Google One Tap user data
 * Follows the same patterns as other authentication actions in accountActions.ts
 */
async function pianoAuthWithGoogleAction(payload: GoogleUserPayload): Promise<PianoAuthResult> {
  try {
    const { email, givenName, familyName } = payload;

    // Validate payload
    const validationResult = getPayloadValidationResult(payload);
    if (validationResult) {
      return validationResult;
    }

    // Check if user exists or register
    const { pianoUser, isNewUser, error: userError } = await getOrRegisterPianoUser(email, givenName, familyName);
    if (!pianoUser) {
      return {
        message: userError || 'User registration failed',
        success: false,
      };
    }

    // Generate Piano user token
    const { pianoToken, error: tokenError } = await getPianoUserToken(pianoUser.uid);
    if (!pianoToken) {
      return {
        message: tokenError || 'Token generation failed',
        success: false,
      };
    }

    return {
      isNewUser,
      message: isNewUser
        ? 'User registered successfully in Piano via Google'
        : 'User authenticated successfully in Piano via Google',
      pianoToken,
      pianoUid: pianoUser.uid,
      success: true,
    };
  } catch (error) {
    logApiError(error as Error, '[pianoAuthWithGoogleAction]');
    return {
      message: 'An unexpected error occurred during Piano authentication',
      success: false,
    };
  }
}

export { pianoAuthWithGoogleAction };
export type { PianoAuthResult };
